import { ThemeTokensSchema } from "@klarity/themes";
import type { FontInfo } from "../../../lib/api/models";

export type FieldKind = "color" | "pt" | "mm" | "bool" | "text";
export type TokenField = { path: string; label: string; kind: FieldKind; group: string };

const f = (group: string, kind: FieldKind, prefix: string, rows: Array<[string, string]>): TokenField[] =>
  rows.map(([key, label]) => ({ path: `${prefix}.${key}`, label, kind, group }));

const LABELS: Array<[string, string]> = [
  ["internal", "Internal classification"],
  ["clientCover", "Client cover line (use {client})"],
  ["clientFooter", "Client footer"],
  ["page", "Page label"],
  ["contents", "Contents title"],
  ["note", "Note label"],
  ["warning", "Warning label"],
];

export const TOKEN_FIELDS: TokenField[] = [
  ...f("Colours", "color", "colors", [
    ["primary", "Primary: headings, table headers, rules"],
    ["accent", "Accent: heading numbers, cover subtitle, warnings"],
    ["text", "Body text"],
    ["muted", "Muted: cover meta, header and footer"],
    ["code", "Code"],
    ["border", "Borders"],
    ["stripe", "Table stripe"],
    ["caption", "Text on primary"],
    ["noteFill", "Note background"],
    ["warnFill", "Warning background"],
    ["white", "Page background"],
  ]),
  ...f("Type sizes", "pt", "sizes", [
    ["body", "Body"],
    ["table", "Table"],
    ["group", "Group list"],
    ["groupTitle", "Group title"],
    ["groupMeta", "Group meta"],
    ["code", "Code"],
    ["small", "Small (header, footer)"],
    ["title", "Page title"],
    ["h1", "Heading 1"],
    ["h2", "Heading 2"],
    ["coverSubtitle", "Cover subtitle"],
    ["coverDocType", "Cover document type"],
  ]),
  ...f("Page", "mm", "page", [
    ["marginMm", "Margin"],
    ["headerMm", "Header distance"],
    ["footerMm", "Footer distance"],
  ]),
  ...f("Spacing", "pt", "spacing.after", [
    ["h1", "After heading 1"],
    ["h2", "After heading 2"],
    ["p", "After paragraph"],
    ["kv", "After key–value"],
    ["bullets", "After bullets"],
    ["steps", "After steps"],
    ["code", "After code"],
  ]),
  ...f("Spacing", "pt", "spacing.firstBefore", [
    ["h1", "Heading 1 at the top of the body"],
    ["h2", "Heading 2 at the top of the body"],
  ]),
  ...f("Spacing", "pt", "spacing.gap", [
    ["afterH1", "Gap after heading 1"],
    ["afterH2", "Gap after heading 2"],
    ["h1AfterTable", "Heading 1 after a table"],
    ["h1AfterText", "Heading 1 after text"],
    ["h2AfterTable", "Heading 2 after a table"],
    ["h2AfterText", "Heading 2 after text"],
    ["pAfterP", "Paragraph after paragraph"],
    ["textAfterText", "Text after text"],
    ["aroundTable", "Around tables"],
  ]),
  { path: "table.stripes", label: "Striped rows", kind: "bool", group: "Tables" },
  ...f("Tables", "pt", "table.cellPadding", [
    ["v", "Cell padding, vertical"],
    ["h", "Cell padding, horizontal"],
  ]),
  ...f("Labels (English)", "text", "labels.en", LABELS),
  ...f("Labels (Dutch)", "text", "labels.nl", LABELS),
];

export function getAt(obj: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (o, k) => (o !== null && typeof o === "object" ? (o as Record<string, unknown>)[k] : undefined),
      obj,
    );
}

export function setAt<T>(obj: T, path: string, value: unknown): T {
  const [head, ...rest] = path.split(".");
  if (head === undefined) return obj;
  const o = (obj ?? {}) as Record<string, unknown>;
  return { ...o, [head]: rest.length ? setAt(o[head], rest.join("."), value) : value } as T;
}

const COLOR_PATH = /^colors\./;

export function tokenErrors(tokens: unknown): Record<string, string> {
  const r = ThemeTokensSchema.safeParse(tokens);
  if (r.success) return {};
  const out: Record<string, string> = {};
  for (const issue of r.error.issues) {
    const path = issue.path.map(String).join(".");
    out[path] ??= COLOR_PATH.test(path) ? "Use #RRGGBB." : issue.message;
  }
  return out;
}

const ORDER = ["regular", "bold", "italic", "bold italic"];
const rank = (subfamily: string) => {
  const i = ORDER.indexOf(subfamily.toLowerCase());
  return i < 0 ? ORDER.length : i;
};

export function fontFamilies(fonts: FontInfo[]): Array<{ family: string; files: string[] }> {
  const byFamily = new Map<string, FontInfo[]>();
  for (const font of fonts) byFamily.set(font.family, [...(byFamily.get(font.family) ?? []), font]);
  return [...byFamily.entries()].map(([family, list]) => ({
    family,
    files: [...list].sort((a, b) => rank(a.subfamily) - rank(b.subfamily)).map((x) => x.src),
  }));
}
